import { store } from '../redux/store';
import { ebioroApi } from '../api/ebioro/ebioro';
import * as Sentry from '@sentry/react';

export type TransactionStatus = 'pending' | 'surge_pricing' | 'processing' | 'completed' | 'error';

export interface TransactionTrackingResult {
  status: TransactionStatus;
  message?: string;
  data?: any;
}

export interface TransactionTrackingCallbacks {
  onStatusChange?: (status: TransactionStatus, message?: string) => void;
  onSurgePricing?: () => void;
  onProcessing?: () => void;
  onCompleted?: (data?: any) => void;
  onError?: (error: string) => void;
}

export class TransactionTracker {
  private static instance: TransactionTracker;
  private activeTrackers: Map<string, boolean> = new Map();
  private pollingInterval: number = 4000; // 4 seconds

  private constructor() {}

  public static getInstance(): TransactionTracker {
    if (!TransactionTracker.instance) {
      TransactionTracker.instance = new TransactionTracker();
    }
    return TransactionTracker.instance;
  }

  /**
   * Track a transaction status with callbacks
   */
  public async trackTransaction(
    transactionId: string,
    callbacks: TransactionTrackingCallbacks = {}
  ): Promise<TransactionTrackingResult> {
    // Mark this transaction as being tracked
    this.activeTrackers.set(transactionId, true);

    try {
      let result = await this.fetchTransactionStatus(transactionId);

      // Initial status callback
      callbacks.onStatusChange?.(result.status, result.status_message);

      // Continue polling until transaction reaches a final state
      while (
        this.activeTrackers.get(transactionId) && 
        !['processing', 'completed', 'error'].includes(result.status)
      ) {
        if (result.status === 'surge_pricing') {
          callbacks.onSurgePricing?.();
          callbacks.onStatusChange?.(result.status, result.status_message);
        }

        await this.sleep(this.pollingInterval);
        
        // Check if tracking was stopped
        if (!this.activeTrackers.get(transactionId)) {
          break;
        }
        
        result = await this.fetchTransactionStatus(transactionId);
        callbacks.onStatusChange?.(result.status, result.status_message);
      }

      // Handle final states
      if (this.activeTrackers.get(transactionId)) {
        if (result.status === 'error') {
          const errorMessage = result.status_message === 'submission_error' 
            ? 'submission_error' 
            : 'transaction_error';
          
          callbacks.onError?.(errorMessage);
          
          return {
            status: 'error',
            message: errorMessage,
            data: result
          };
        } else if (result.status === 'completed') {
          callbacks.onCompleted?.(result);
          
          return {
            status: 'completed',
            message: 'Transaction completed successfully',
            data: result
          };
        } else if (result.status === 'processing') {
          callbacks.onProcessing?.();
          
          return {
            status: 'processing',
            message: 'Transaction is being processed',
            data: result
          };
        }
      }

      return {
        status: result.status as TransactionStatus,
        message: result.status_message,
        data: result
      };

    } catch (error) {
      Sentry.captureException(error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      callbacks.onError?.(errorMessage);
      
      return {
        status: 'error',
        message: errorMessage
      };
    } finally {
      // Clean up tracking
      this.activeTrackers.delete(transactionId);
    }
  }

  /**
   * Stop tracking a specific transaction
   */
  public stopTracking(transactionId: string): void {
    this.activeTrackers.set(transactionId, false);
  }

  /**
   * Stop all active tracking
   */
  public stopAllTracking(): void {
    this.activeTrackers.forEach((_, transactionId) => {
      this.activeTrackers.set(transactionId, false);
    });
  }

  /**
   * Check if a transaction is being tracked
   */
  public isTracking(transactionId: string): boolean {
    return this.activeTrackers.get(transactionId) === true;
  }

  /**
   * Get single transaction status (one-time fetch)
   */
  public async getTransactionStatus(transactionId: string): Promise<TransactionTrackingResult> {
    try {
      const result = await this.fetchTransactionStatus(transactionId);
      
      return {
        status: result.status as TransactionStatus,
        message: result.status_message,
        data: result
      };
    } catch (error) {
      Sentry.captureException(error);
      
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch transaction status'
      };
    }
  }

  /**
   * Set polling interval (in milliseconds)
   */
  public setPollingInterval(interval: number): void {
    this.pollingInterval = interval;
  }

  /**
   * Private method to fetch transaction status from API
   */
  private async fetchTransactionStatus(transactionId: string): Promise<any> {
    return store.dispatch(
      ebioroApi.endpoints.getTransaction.initiate(transactionId, { forceRefetch: true })
    ).unwrap();
  }

  /**
   * Private sleep utility
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const transactionTracker = TransactionTracker.getInstance();